import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ExternalLink, FileText, Loader2, Receipt } from "lucide-react";
import { apiError, recruitersAdminApi } from "../api";
import { money, periodLabel, previousPeriod, STATEMENT_STATUS } from "../format";
import { Badge, Empty, Field, Modal } from "../ui";
import type { Statement } from "../types";
import { LoadingBlock, ErrorBlock } from "../../dashboard/shared/StateBlocks";
import { useToast } from "@/app/providers/Toaster";
import { Can } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";

function chileToday(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Santiago" }).format(new Date());
}

/**
 * Liquidaciones mensuales (B9): se generan con el mes cerrado, el reclutador
 * sube su boleta por el bruto y Mixcore paga el líquido con comprobante.
 */
export function StatementsPane() {
  const { push } = useToast();
  const qc = useQueryClient();
  const [period, setPeriod] = useState(previousPeriod(chileToday()));
  const [paying, setPaying] = useState<Statement | null>(null);
  const [observing, setObserving] = useState<Statement | null>(null);
  const q = useQuery({ queryKey: ["recruiters", "statements", period], queryFn: () => recruitersAdminApi.statements(period) });

  const gen = useMutation({
    mutationFn: () => recruitersAdminApi.generateStatements(period),
    onSuccess: (r) => {
      const msg =
        r.created || r.updated
          ? `${r.created} liquidación${r.created === 1 ? "" : "es"} nueva${r.created === 1 ? "" : "s"}${r.updated ? ` y ${r.updated} actualizada${r.updated === 1 ? "" : "s"}` : ""}. Les avisamos por correo.`
          : "No había comisiones por liquidar en ese mes.";
      push({ kind: "success", message: msg });
      for (const s of r.skipped) push({ kind: "info", message: s.reason });
      qc.invalidateQueries({ queryKey: ["recruiters"] });
    },
    onError: (err) => push({ kind: "error", message: apiError(err) }),
  });

  const closed = period < chileToday().slice(0, 7);
  const pendingTotal = (q.data?.pendingToGenerate ?? []).reduce((s, p) => s + p.amount, 0);

  return (
    <div className="space-y-4">
      <div className="nx-card p-4 flex flex-wrap items-end gap-3 justify-between">
        <Field label="Mes">
          <input className="nx-input w-44" type="month" value={period} onChange={(e) => e.target.value && setPeriod(e.target.value)} />
        </Field>
        <div className="flex flex-col items-end gap-1">
          {pendingTotal > 0 && (
            <span className="text-xs text-fg-muted">
              Por liquidar en {periodLabel(period)}: <b className="text-fg tabular-nums">{money(pendingTotal)}</b>
            </span>
          )}
          <Can permission={PERMISSIONS.RECRUITERS_PAY}>
            <button type="button" className="nx-btn-primary" disabled={!closed || gen.isPending} onClick={() => gen.mutate()}>
              {gen.isPending ? <Loader2 size={14} className="animate-spin" /> : <Receipt size={14} />}
              Generar liquidaciones de {periodLabel(period)}
            </button>
          </Can>
          {!closed && <span className="text-xs text-fg-subtle">Se liquidan sólo meses cerrados.</span>}
        </div>
      </div>

      {q.isLoading && <LoadingBlock label="Cargando liquidaciones…" />}
      {q.isError && <ErrorBlock onRetry={() => q.refetch()} />}
      {q.data && q.data.statements.length === 0 && <Empty>No hay liquidaciones de {periodLabel(period)}.</Empty>}
      {q.data && q.data.statements.length > 0 && (
        <ul className="grid gap-3">
          {q.data.statements.map((s) => (
            <StatementRow key={s.id} s={s} onPay={() => setPaying(s)} onObserve={() => setObserving(s)} />
          ))}
        </ul>
      )}
      {paying && <PayModal s={paying} onClose={() => setPaying(null)} />}
      {observing && <ObserveInvoiceModal s={observing} onClose={() => setObserving(null)} />}
    </div>
  );
}

export function StatementRow({ s, onPay, onObserve, hideName }: { s: Statement; onPay?: () => void; onObserve?: () => void; hideName?: boolean }) {
  const navigate = useNavigate();
  const st = STATEMENT_STATUS[s.status];
  return (
    <li className="nx-card p-4 grid gap-3 sm:grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,0.8fr))_auto] sm:items-center">
      <div className="min-w-0">
        {!hideName && (
          <button type="button" className="font-semibold text-fg hover:underline text-left truncate block" onClick={() => navigate(`/reclutadores/${s.recruiterId}?tab=liquidaciones`)}>
            {s.recruiterName}
          </button>
        )}
        <div className="flex items-center gap-2 text-xs text-fg-muted mt-0.5">
          <span className="capitalize">{s.periodLabel}</span>
          <Badge tone={st.tone}>{st.label}</Badge>
        </div>
        {s.invoiceNote && s.status === "awaiting_invoice" && <div className="text-xs text-warning mt-1">Boleta observada: {s.invoiceNote}</div>}
      </div>
      <Amount label="Bruto (boleta)" value={money(s.gross)} />
      <Amount label={`Retención ${s.retentionPct.toLocaleString("es-CL")} %`} value={money(s.retention)} />
      <Amount label="Líquido" value={money(s.net)} strong />
      <div className="flex flex-wrap gap-2 sm:justify-end">
        {s.invoiceUrl && (
          <a className="nx-btn-ghost" href={s.invoiceUrl} target="_blank" rel="noreferrer">
            <FileText size={14} /> Boleta {s.invoiceNumber ? `n.º ${s.invoiceNumber}` : ""}
          </a>
        )}
        {s.receiptUrl && (
          <a className="nx-btn-ghost" href={s.receiptUrl} target="_blank" rel="noreferrer">
            <ExternalLink size={14} /> Comprobante
          </a>
        )}
        {s.status === "invoice_submitted" && (
          <Can permission={PERMISSIONS.RECRUITERS_PAY}>
            {onObserve && (
              <button type="button" className="nx-btn-secondary" onClick={onObserve}>
                Observar boleta
              </button>
            )}
            {onPay && (
              <button type="button" className="nx-btn-primary" onClick={onPay}>
                Marcar pagada
              </button>
            )}
          </Can>
        )}
        {s.status === "paid" && s.paymentReference && <span className="text-xs text-fg-subtle self-center">Transf. {s.paymentReference}</span>}
      </div>
    </li>
  );
}

function Amount({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <div className="text-xs text-fg-muted">{label}</div>
      <div className={strong ? "text-sm font-bold text-fg tabular-nums" : "text-sm text-fg tabular-nums"}>{value}</div>
    </div>
  );
}

export function PayModal({ s, onClose }: { s: Statement; onClose: () => void }) {
  const { push } = useToast();
  const qc = useQueryClient();
  const [reference, setReference] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const mut = useMutation({
    mutationFn: () => recruitersAdminApi.payStatement(s.id, reference.trim(), file),
    onSuccess: () => {
      push({ kind: "success", message: "Liquidación pagada. Le avisamos por correo." });
      qc.invalidateQueries({ queryKey: ["recruiters"] });
      onClose();
    },
    onError: (err) => push({ kind: "error", message: apiError(err) }),
  });
  return (
    <Modal
      title={`Pagar ${s.periodLabel}${s.recruiterName ? ` · ${s.recruiterName}` : ""}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="nx-btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="nx-btn-primary" disabled={!reference.trim() || mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending && <Loader2 size={14} className="animate-spin" />}
            Marcar pagada
          </button>
        </>
      }
    >
      <p className="text-sm text-fg-muted">
        Transfiere el <b className="text-fg">líquido {money(s.net)}</b>. La retención de {money(s.retention)} la declara y
        entera Go Up Cloud SpA.
      </p>
      <Field label="N.º de la transferencia" required>
        <input className="nx-input" value={reference} onChange={(e) => setReference(e.target.value)} autoFocus />
      </Field>
      <Field label="Comprobante (opcional)" hint="PDF, JPG o PNG de hasta 5 MB.">
        <input type="file" accept="application/pdf,image/jpeg,image/png" className="text-sm" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      </Field>
    </Modal>
  );
}

export function ObserveInvoiceModal({ s, onClose }: { s: Statement; onClose: () => void }) {
  const { push } = useToast();
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const mut = useMutation({
    mutationFn: () => recruitersAdminApi.observeInvoice(s.id, note.trim()),
    onSuccess: () => {
      push({ kind: "success", message: "Le avisamos que corrija la boleta." });
      qc.invalidateQueries({ queryKey: ["recruiters"] });
      onClose();
    },
    onError: (err) => push({ kind: "error", message: apiError(err) }),
  });
  return (
    <Modal
      title="Observar boleta"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="nx-btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="nx-btn-primary" disabled={!note.trim() || mut.isPending} onClick={() => mut.mutate()}>
            Enviar observación
          </button>
        </>
      }
    >
      <Field label="¿Qué hay que corregir?" required hint={`La boleta va por el bruto (${money(s.gross)}) a Go Up Cloud SpA.`}>
        <textarea className="nx-input min-h-[80px]" value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
      </Field>
    </Modal>
  );
}
